import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Dialog,
  Pane,
  Spinner,
  Heading,
  Text,
  Button,
  Alert,
  Table,
  Badge,
  toaster,
  TextareaField,
  TextInputField,
  Popover,
  Button as EvergreenButton
} from 'evergreen-ui';
import { GET_PUBLISHER_STATS } from '../../graphql/queries/Publishers/QueryPublishers';
import { CONTACT_PUBLISHER, RESET_PUBLISHER_PASSWORD, DELETE_PUBLISHER } from '../../graphql/queries/Auth/QueryAuth';
import { getCookie } from '../../util';
import CustomButton from '../common/CustomButton';

/**
 * PublisherStatsModal component
 * Displays detailed stats for a publisher in a modal
 *
 * @param {Object} props
 * @param {boolean} props.isShown - Whether the modal is shown
 * @param {Function} props.onClose - Callback when the modal is closed
 * @param {Object} props.publisher - The publisher object
 */
const PublisherStatsModal = ({ isShown, onClose, publisher }) => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [creatorCount, setCreatorCount] = useState(0);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubject, setContactSubject] = useState('Message from WorkingLit Admin');
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

  // Debug publisher data
  useEffect(() => {
    if (isShown) {
      // Removed debug logs
    }
  }, [isShown, publisher]);

  // Fetch publisher stats
  const { loading, error, data, refetch } = useQuery(GET_PUBLISHER_STATS, {
    variables: { publisherId: publisher?.id ? parseInt(publisher.id, 10) : null },
    skip: !publisher?.id,
    fetchPolicy: 'network-only', // Don't use cache
  });

  // Contact publisher mutation
  const [contactPublisher, { loading: contactLoading }] = useMutation(CONTACT_PUBLISHER, {
    onCompleted: () => {
      toaster.success('Message sent successfully', {
        description: `Your message has been sent to ${publisher?.name}`
      });
      setIsContactDialogOpen(false);
      setContactMessage('');
      setContactSubject('Message from WorkingLit Admin');
    },
    onError: (error) => {
      toaster.danger('Failed to send message', {
        description: error.message
      });
    }
  });

  // Reset password mutation
  const [resetPassword, { loading: resetLoading }] = useMutation(RESET_PUBLISHER_PASSWORD, {
    onCompleted: () => {
      toaster.success('Password reset link sent', {
        description: `A password reset link has been sent to the publisher's email`
      });
      setIsResetConfirmOpen(false);
    },
    onError: (error) => {
      toaster.danger('Failed to reset password', {
        description: error.message
      });
    }
  });

  // Delete publisher mutation
  const [deletePublisher, { loading: deleteLoading }] = useMutation(DELETE_PUBLISHER, {
    onCompleted: () => {
      toaster.success('Publisher deleted successfully', {
        description: `${publisher?.name} and all associated data has been permanently deleted`
      });
      setIsDeleteConfirmOpen(false);
      onClose(); // Close the modal
      // Optionally refresh the page or update the parent component
      window.location.reload(); // Simple approach for now
    },
    onError: (error) => {
      toaster.danger('Failed to delete publisher', {
        description: error.message
      });
      setIsDeleteConfirmOpen(false);
    }
  });

  // Fetch creator count separately if needed
  useEffect(() => {
    if (isShown && publisher?.id) {
      // Fetch creator count using the API
      const fetchCreatorCount = async () => {
        try {
          const csrftoken = getCookie('csrftoken');
          const response = await fetch('/count_creators/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrftoken,
              'Authorization': `Token ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ publisher: String(publisher.id) })
          });

          // Check if the response is OK
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          // Check the content type to ensure it's JSON
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            // Removed debug logs
            throw new Error('Response is not JSON');
          }

          const result = await response.json();
          if (result.message) {
            const count = parseInt(result.message, 10);
            setCreatorCount(count);
          }
        } catch (error) {
          console.error('Error fetching creator count:', error);
          // Set a default value in case of error
          setCreatorCount(0);
        }
      };

      fetchCreatorCount();
    }
  }, [isShown, publisher]);

  // Debug query data
  useEffect(() => {
    if (isShown && publisher?.id) {
      // Force refetch when modal is shown
      refetch({ publisherId: parseInt(publisher.id, 10) });
    }
  }, [isShown, publisher, loading, error, data, refetch]);

  // Parse the JSON stats data - handle double JSON encoding
  const stats = data?.publisherStats ? (() => {
    try {
      // The response is a JSON string containing another JSON string
      const parsedOnce = JSON.parse(data.publisherStats);

      // Check if the result is still a string (double encoded)
      if (typeof parsedOnce === 'string') {
        try {
          return JSON.parse(parsedOnce);
        } catch (e) {
          console.error('Error parsing inner JSON:', e);
          return parsedOnce;
        }
      }
      return parsedOnce;
    } catch (e) {
      console.error('Error parsing JSON:', e);
      return null;
    }
  })() : null;

  // Debug parsed stats
  useEffect(() => {
    if (stats) {
      // Stats parsed successfully
    }
  }, [stats, creatorCount]);

  // Helper function to get stats value with fallback for different field naming conventions
  const getStat = (key, fallbackKey, defaultValue = 0) => {
    if (!stats) return defaultValue;
    return stats[key] !== undefined ? stats[key] :
           (stats[fallbackKey] !== undefined ? stats[fallbackKey] : defaultValue);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Handle contacting the publisher
  const handleContactPublisher = () => {
    if (!publisher?.id || !contactMessage.trim()) return;

    contactPublisher({
      variables: {
        publisherId: String(publisher.id),
        message: contactMessage.trim(),
        subject: contactSubject.trim() || 'Message from WorkingLit Admin'
      }
    });
  };

  // Handle resetting the publisher's password
  const handleResetPassword = () => {
    if (!publisher?.id) return;

    resetPassword({
      variables: {
        publisherId: String(publisher.id)
      }
    });
  };

  // Handle deleting the publisher
  const handleDeletePublisher = () => {
    if (!publisher?.id) return;

    deletePublisher({
      variables: {
        id: parseInt(publisher.id, 10)
      }
    });
  };

  // Handle impersonating the publisher
  const handleImpersonate = async () => {
    if (!publisher?.id) return;

    setIsImpersonating(true);
    const csrftoken = getCookie('csrftoken');
    const superuser = localStorage.getItem('userid');

    try {
      // Get the owner user ID from stats if available
      let userIdToImpersonate = null;

      if (stats && stats.owner_id) {
        userIdToImpersonate = stats.owner_id;
        console.log('Impersonating owner user ID:', userIdToImpersonate);
      } else {
        // Fallback to publisher ID if owner ID is not available
        alert('No owner ID found for impersonation');
        console.log('Falling back to publisher ID for impersonation:', userIdToImpersonate);
      }

      // Call the impersonate API
      const response = await fetch('/personate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        },
        body: JSON.stringify({
          user: {
            superuser: superuser,
            number: userIdToImpersonate
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Impersonate response not OK:', response.status, errorText);
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('Impersonate response:', data);

      if (data.token) {
        toaster.success('Successfully impersonating publisher', {
          description: `You are now logged in as ${publisher.name}`
        });

        // Clear localStorage and set new token
        localStorage.clear();
        localStorage.setItem('token', data.token);

        // Fetch user publishers
        const csrftoken1 = getCookie('csrftoken');
        const publishersResponse = await fetch('/fetch_user_publishers/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken1
          },
          body: JSON.stringify({ token: data.token })
        });

        if (!publishersResponse.ok) {
          const errorText = await publishersResponse.text();
          console.error('Fetch publishers response not OK:', publishersResponse.status, errorText);
          throw new Error(`Server returned ${publishersResponse.status}: ${errorText}`);
        }

        const publishersData = await publishersResponse.json();
        console.log('Publishers data:', publishersData);

        // Set user and publisher data in localStorage
        localStorage.setItem('userid', publishersData.user.id);
        localStorage.setItem('publisher', publishersData.publisher.id);
        localStorage.setItem('publisher_name', publishersData.publisher.name);

        // Redirect to dashboard
        window.location.replace('/dashboard');
      } else if (data.error) {
        throw new Error(data.error);
      } else {
        throw new Error('Failed to impersonate user - no token returned');
      }
    } catch (error) {
      console.error('Error impersonating publisher:', error);
      toaster.danger('Failed to impersonate publisher', {
        description: error.message
      });
      setIsImpersonating(false);
    }
  };

  // Contact publisher dialog
  const ContactDialog = () => (
    <Dialog
      isShown={isContactDialogOpen}
      title={`Contact ${publisher?.name || 'Publisher'}`}
      onCloseComplete={() => setIsContactDialogOpen(false)}
      confirmLabel="Send Message"
      isConfirmLoading={contactLoading}
      onConfirm={handleContactPublisher}
      intent="success"
    >
      <TextInputField
        label="Subject"
        value={contactSubject}
        onChange={e => setContactSubject(e.target.value)}
        placeholder="Enter message subject"
      />
      <TextareaField
        label="Message"
        value={contactMessage}
        onChange={e => setContactMessage(e.target.value)}
        placeholder="Enter your message to the publisher"
        required
        minHeight={100}
      />
    </Dialog>
  );

  return (
    <>
      <Dialog
        isShown={isShown}
        title={`Publisher Stats: ${publisher?.name || ''}`}
        onCloseComplete={onClose}
        width="800px"
        hasFooter={false}
      >
        {loading && (
          <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
            <Spinner />
          </Pane>
        )}

        {error && (
          <Alert intent="danger" title="Error loading publisher stats">
            {error.message}
          </Alert>
        )}

        {!loading && !error && (
          <Pane>
            <Pane marginBottom={24}>
              <Heading size={500} marginBottom={8}>Publisher Information</Heading>
              <Table>
                <Table.Body>
                  <Table.Row>
                    <Table.TextCell fontWeight="bold">First Signed Up</Table.TextCell>
                    <Table.TextCell>
                      {formatDate(getStat('first_signed_up', 'firstSignedUp') || publisher?.created)}
                    </Table.TextCell>
                  </Table.Row>
                  <Table.Row>
                    <Table.TextCell fontWeight="bold">Last Login</Table.TextCell>
                    <Table.TextCell>
                      {publisher?.lastLogin ? formatDate(publisher.lastLogin) :
                       (getStat('last_login', 'lastLogin') ? formatDate(getStat('last_login', 'lastLogin')) : 'Never')}
                    </Table.TextCell>
                  </Table.Row>
                  <Table.Row>
                    <Table.TextCell fontWeight="bold">Last Activity</Table.TextCell>
                    <Table.TextCell>
                      {publisher?.lastActivity ? formatDate(publisher.lastActivity) :
                       (getStat('last_activity', 'lastActivity') ? formatDate(getStat('last_activity', 'lastActivity')) : 'Never')}
                    </Table.TextCell>
                  </Table.Row>
                  <Table.Row>
                    <Table.TextCell fontWeight="bold">Last Activity Type</Table.TextCell>
                    <Table.TextCell>
                      {publisher?.lastActivityType || getStat('last_activity_type', 'lastActivityType') ? (
                        <Badge color="blue">{publisher?.lastActivityType || getStat('last_activity_type', 'lastActivityType')}</Badge>
                      ) : (
                        'None'
                      )}
                    </Table.TextCell>
                  </Table.Row>
                  <Table.Row>
                    <Table.TextCell fontWeight="bold">Created</Table.TextCell>
                    <Table.TextCell>{formatDate(publisher?.created)}</Table.TextCell>
                  </Table.Row>
                  <Table.Row>
                    <Table.TextCell fontWeight="bold">Last Modified</Table.TextCell>
                    <Table.TextCell>{formatDate(publisher?.modified)}</Table.TextCell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Pane>

            <Pane marginBottom={24}>
              <Heading size={500} marginBottom={8}>Publisher Stats</Heading>
              <Pane display="flex" flexWrap="wrap">
                <Pane width="50%" padding={8}>
                  <Pane background="tint1" padding={16} borderRadius={4} height="100%">
                    <Heading size={100} marginBottom={4}>TOTAL PRODUCTS</Heading>
                    {getStat('total_products', 'product_count')}
                  </Pane>
                </Pane>
                <Pane width="50%" padding={8}>
                  <Pane background="tint1" padding={16} borderRadius={4} height="100%">
                    <Heading size={100} marginBottom={4}>TOTAL ORDERS</Heading>
                    {getStat('total_orders', 'order_count')}
                  </Pane>
                </Pane>
                <Pane width="50%" padding={8}>
                  <Pane background="tint1" padding={16} borderRadius={4} height="100%">
                    <Heading size={100} marginBottom={4}>TOTAL CREATORS</Heading>
                    {getStat('total_creators', 'creator_count')}                  </Pane>
                </Pane>
                <Pane width="50%" padding={8}>
                  <Pane background="tint1" padding={16} borderRadius={4} height="100%">
                    <Heading size={100} marginBottom={4}>TOTAL CUSTOMERS</Heading>
                    {getStat('total_customers', 'customer_count')}

                  </Pane>
                </Pane>
              </Pane>
            </Pane>

            <Pane display="flex" justifyContent="flex-end" marginTop={16}>
              <CustomButton
                appearance="primary"
                intent="danger"
                onClick={() => window.location.href = `mailto:${stats?.owner_email || 'admin@workinglit.com'}`}
                marginRight={8}
              >
                Email: {stats?.owner_email || 'admin@workinglit.com'}
              </CustomButton>

              <Popover
                content={
                  <Pane padding={16} width={300}>
                    <Heading size={400} marginBottom={8}>Reset Password</Heading>
                    <Text marginBottom={16}>
                      This will send a password reset link to the publisher's email address. Are you sure you want to continue?
                    </Text>
                    <Pane display="flex" justifyContent="flex-end">
                      <CustomButton
                        appearance="primary"
                        intent="danger"
                        onClick={handleResetPassword}
                        isLoading={resetLoading}
                      >
                        Reset Password
                      </CustomButton>
                    </Pane>
                  </Pane>
                }
                onOpen={() => setIsResetConfirmOpen(true)}
                onClose={() => setIsResetConfirmOpen(false)}
                isShown={isResetConfirmOpen}
              >
                <CustomButton
                  appearance="primary"
                  intent="danger"
                  onClick={() => setIsResetConfirmOpen(true)}
                  marginRight={8}
                >
                  Reset Password
                </CustomButton>
              </Popover>

              <CustomButton
                appearance="primary"
                intent="warning"
                onClick={handleImpersonate}
                isLoading={isImpersonating}
                marginRight={8}
              >
                Log in as this publisher-user
              </CustomButton>

              <EvergreenButton onClick={onClose}>Close</EvergreenButton>
            </Pane>

            {/* Delete Publisher Section - Separated for emphasis */}
            <Pane 
              marginTop={32} 
              paddingTop={16} 
              borderTop="1px solid #ddd"
              display="flex" 
              justifyContent="center"
            >
              {console.log('Rendering delete section, isDeleteConfirmOpen:', isDeleteConfirmOpen)}
              <EvergreenButton
                appearance="primary"
                intent="danger"
                onClick={() => {
                  console.log('Delete button clicked!');
                  setIsDeleteConfirmOpen(true);
                }}
                size="large"
              >
                🗑️ Delete this publisher
              </EvergreenButton>
            </Pane>

            {/* Delete Confirmation Dialog */}
            {isDeleteConfirmOpen && (
              <Dialog
                isShown={isDeleteConfirmOpen}
                title="⚠️ Danger Zone - Delete Publisher"
                onCloseComplete={() => {
                  setIsDeleteConfirmOpen(false);
                  setDeleteConfirmationText('');
                }}
                confirmLabel="Delete Publisher Forever"
                cancelLabel="Cancel"
                onConfirm={handleDeletePublisher}
                isConfirmLoading={deleteLoading}
                isConfirmDisabled={deleteConfirmationText !== publisher?.name}
                intent="danger"
              >
                <Text marginBottom={16}>
                  <strong>This action cannot be undone.</strong> This will permanently delete:
                </Text>
                <ul style={{marginBottom: 16, paddingLeft: 20}}>
                  <li>Publisher: {publisher?.name}</li>
                  <li>All {getStat('total_products', 'product_count')} products</li>
                  <li>All {getStat('total_orders', 'order_count')} orders</li>
                  <li>All {getStat('total_creators', 'creator_count')} creators</li>
                  <li>All {getStat('total_customers', 'customer_count')} customers</li>
                  <li>All associated user accounts (if not shared with other publishers)</li>
                  <li>All financial records, royalties, and payments</li>
                </ul>
                <Text marginBottom={16} color="red">
                  Type the publisher name to confirm deletion:
                </Text>
                <TextInputField
                  placeholder={`Type "${publisher?.name}" to confirm`}
                  value={deleteConfirmationText}
                  onChange={e => setDeleteConfirmationText(e.target.value)}
                />
              </Dialog>
            )}
          </Pane>
        )}
      </Dialog>

      {/* Contact Publisher Dialog */}
      <ContactDialog />
    </>
  );
};

export default PublisherStatsModal;
